import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import './LaunchModal.css';

interface Launch {
  id: string;
  name: string;
  provider: string;
  providerLogo?: string;
  launchTime: string;
  launchSite: string;
  launchPad?: string;
  missionType: string;
  description?: string;
  status: 'GO' | 'HOLD' | 'TBD' | 'SCRUB';
  streamUrl?: string;
  weatherGo?: number;
  vehicleType?: string;
  missionPatch?: string;
  payload?: string;
  orbit?: string;
}

interface LaunchModalProps {
  launch: Launch;
  onClose: () => void;
}

// Default launch object with placeholder data
const defaultLaunch: Launch = {
  id: 'placeholder',
  name: 'Next Space Mission',
  provider: 'Space Agency',
  launchTime: new Date(Date.now() + 86400000 * 3).toISOString(), // 3 days from now
  launchSite: 'Kennedy Space Center',
  missionType: 'Satellite Deploy',
  status: 'TBD',
  description: 'An exciting space mission launching soon. Check back for more details.',
  vehicleType: 'Heavy Lift Vehicle',
  payload: 'Communications Satellite',
  orbit: 'Geostationary Transfer Orbit',
  weatherGo: 75
};

const LaunchModal: React.FC<LaunchModalProps> = ({ launch: providedLaunch, onClose }) => {
  // Merge provided launch data with defaults to ensure all fields have values
  const launch = { ...defaultLaunch, ...providedLaunch };
  
  const [weather, setWeather] = useState<any>(null);
  const [countdown, setCountdown] = useState('');
  const [countdownParts, setCountdownParts] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [activeTab, setActiveTab] = useState<'details' | 'weather' | 'tracking' | 'trajectory'>('details');
  const [showLaunchAnimation, setShowLaunchAnimation] = useState(true);
  const [launchStatus, setLaunchStatus] = useState<'upcoming' | 'imminent' | 'launching' | 'launched'>('upcoming');
  const [particleCount, setParticleCount] = useState(50);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Define fetchWeather and updateCountdown using useCallback to avoid dependency cycles
  const fetchWeather = React.useCallback(async () => {
    try {
      const response = await fetch(`/api/weather/launch-site/${encodeURIComponent(launch.launchSite)}`);
      if (response.ok) {
        const data = await response.json();
        setWeather(data);
      } else {
        // Handle non-ok responses
        console.warn(`Weather API returned status ${response.status}`);
        
        // Provide fallback weather data
        setWeather({
          temperature: 72,
          windSpeed: 5,
          conditions: "Data Unavailable",
          weatherGo: launch.weatherGo || 75,
          humidity: 60,
          visibility: "Good",
          updated: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error fetching weather:', error);
      
      // Provide fallback weather data on error
      setWeather({
        temperature: 72,
        windSpeed: 5,
        conditions: "Data Unavailable",
        weatherGo: launch.weatherGo || 75,
        humidity: 60,
        visibility: "Good",
        updated: new Date().toISOString()
      });
    }
  }, [launch.launchSite, launch.weatherGo]);

  const updateCountdown = React.useCallback(() => {
    if (!launch.launchTime) {
      setCountdown('T-TBD');
      setCountdownParts({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const launchTime = new Date(launch.launchTime).getTime();
    const now = new Date().getTime();
    const difference = launchTime - now;

    if (difference > 0) {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setCountdownParts({ days, hours, minutes, seconds });
      setCountdown(`T-${days}d ${hours}h ${minutes}m ${seconds}s`);

      // Update launch status based on time remaining
      if (difference < 60000) { // Less than 1 minute
        setLaunchStatus('launching');
        setParticleCount(200);
      } else if (difference < 3600000) { // Less than 1 hour
        setLaunchStatus('imminent');
        setParticleCount(100);
      } else {
        setLaunchStatus('upcoming');
        setParticleCount(50);
      }
    } else if (difference > -3600000) { // Launch happened less than 1 hour ago
      setCountdown('🚀 LIFTOFF!');
      setLaunchStatus('launched');
      setParticleCount(300);
    } else {
      setCountdown('Mission Complete');
      setLaunchStatus('launched');
      setParticleCount(25);
    }
  }, [launch.launchTime]);

  // Particle animation system
  const initializeParticles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      maxLife: number;
      size: number;
      color: string;
      type: 'star' | 'exhaust' | 'spark';
    }> = [];

    // Create particles based on launch status
    const createParticles = () => {
      for (let i = 0; i < particleCount; i++) {
        const type = launchStatus === 'launching' || launchStatus === 'launched' 
          ? (Math.random() < 0.3 ? 'exhaust' : Math.random() < 0.5 ? 'spark' : 'star')
          : 'star';
        
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * (launchStatus === 'launching' ? 4 : 1),
          vy: launchStatus === 'launching' ? Math.random() * -3 : (Math.random() - 0.5) * 1,
          life: Math.random() * 100,
          maxLife: 100,
          size: Math.random() * (type === 'exhaust' ? 8 : 3) + 1,
          color: type === 'exhaust' ? `hsl(${Math.random() * 60 + 15}, 100%, ${Math.random() * 50 + 50}%)` :
                 type === 'spark' ? '#FFD700' : '#FFFFFF',
          type
        });
      }
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle, index) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.life--;

        if (particle.type === 'exhaust') {
          particle.vy += 0.1; // Gravity effect
        }

        const alpha = particle.life / particle.maxLife;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = particle.color;
        ctx.beginPath();
        
        if (particle.type === 'star') {
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        } else {
          ctx.rect(particle.x, particle.y, particle.size, particle.size);
        }
        
        ctx.fill();

        // Remove dead particles
        if (particle.life <= 0 || particle.y > canvas.height) {
          particles.splice(index, 1);
        }
      });

      ctx.globalAlpha = 1;

      // Add new particles
      if (particles.length < particleCount) {
        const newParticle = particles[0] ? { ...particles[0] } : {
          x: Math.random() * canvas.width,
          y: canvas.height,
          vx: (Math.random() - 0.5) * 2,
          vy: Math.random() * -3,
          life: 100,
          maxLife: 100,
          size: Math.random() * 4 + 1,
          color: '#FFFFFF',
          type: 'star' as const
        };
        
        newParticle.x = Math.random() * canvas.width;
        newParticle.y = launchStatus === 'launching' ? canvas.height : Math.random() * canvas.height;
        newParticle.life = newParticle.maxLife;
        particles.push(newParticle);
      }

      requestAnimationFrame(animate);
    };

    createParticles();
    animate();
  }, [particleCount, launchStatus]);

  useEffect(() => {
    fetchWeather();
    updateCountdown(); // Initial call
    const interval = setInterval(updateCountdown, 1000);
    
    // Initialize particle system
    initializeParticles();
    
    // Remove launch animation after 2 seconds
    const animationTimer = setTimeout(() => {
      setShowLaunchAnimation(false);
    }, 2000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(animationTimer);
    };
  }, [fetchWeather, updateCountdown, initializeParticles]);


  const addToCalendar = () => {
    if (!launch.launchTime) {
      alert('Launch time not yet available');
      return;
    }

    const startTime = new Date(launch.launchTime);
    const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // 1 hour duration
    
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${startTime.toISOString().replace(/[-:]/g, '').replace('.000', '')}
DTEND:${endTime.toISOString().replace(/[-:]/g, '').replace('.000', '')}
SUMMARY:${launch.name || 'Space Launch'} - ${launch.provider || 'Space Agency'}
DESCRIPTION:${launch.description || `${launch.name || 'Mission'} launching from ${launch.launchSite || 'Launch Site'}`}\\n\\nLaunch Site: ${launch.launchSite || 'TBD'}\\nVehicle: ${launch.vehicleType || 'TBD'}
LOCATION:${launch.launchSite || 'Launch Site'}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(launch.name || 'launch').replace(/\s+/g, '_')}.ics`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const shareLaunch = () => {
    const launchTimeText = launch.launchTime 
      ? format(new Date(launch.launchTime), 'PPpp') 
      : 'TBD';
    const shareText = `🚀 ${launch.name || 'Space Mission'} launches ${launchTimeText} from ${launch.launchSite || 'Launch Site'}`;
    const shareUrl = window.location.href;

    if (navigator.share) {
      navigator.share({
        title: launch.name || 'Space Launch',
        text: shareText,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      alert('Launch details copied to clipboard!');
    }
  };

  const getStatusColor = () => {
    switch (launch.status) {
      case 'GO': return '#10b981';
      case 'HOLD': return '#f59e0b';
      case 'SCRUB': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const renderModalContent = () => (
    <>
      {/* Enhanced particle canvas background */}
      <canvas 
        ref={canvasRef} 
        className="particle-canvas"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1
        }}
      />
      
      {/* Audio element for ambient space sounds */}
      <audio 
        ref={audioRef}
        loop
        style={{ display: 'none' }}
      >
        <source src="/sounds/space-ambient.mp3" type="audio/mpeg" />
      </audio>
      
      <div className="modal-stars-bg"></div>
      
      {/* Header Section - Now sticky */}
      <div className="modal-header">
        <button className="modal-close-btn" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="mission-patch-container">
          {launch.missionPatch ? (
            <img src={launch.missionPatch} alt="Mission Patch" className="mission-patch" />
          ) : (
            <div className="mission-patch-placeholder">
              <span className="rocket-icon">🚀</span>
            </div>
          )}
        </div>
        
        <div className="header-info">
          <div className="status-badge" style={{ backgroundColor: getStatusColor() }}>
            {launch.status}
          </div>
          <h2 className="mission-name">{launch.name || 'Upcoming Mission'}</h2>
          <div className="provider-info">
            {launch.providerLogo ? (
              <img src={launch.providerLogo} alt={launch.provider} className="provider-logo" />
            ) : (
              <span className="provider-icon">🛸</span>
            )}
            <span className="provider-name">{launch.provider || 'Space Agency'}</span>
          </div>
        </div>
      </div>

      {/* Content area that scrolls */}
      <div className="modal-body">
          {/* Enhanced Countdown Section */}
          <div className={`countdown-section ${launchStatus}`}>
            <div className="countdown-container">
              <div className="countdown-label">
                {launchStatus === 'launching' ? '🔥 LAUNCH IN PROGRESS' :
                 launchStatus === 'imminent' ? '⚡ LAUNCH IMMINENT' :
                 launchStatus === 'launched' ? '🚀 MISSION COMPLETE' : 'LAUNCH COUNTDOWN'}
              </div>
              
              {/* Dramatic countdown display */}
              <div className="countdown-display-wrapper">
                <div className={`orbit-animation ${launchStatus}`}>
                  <div className="orbit-ring"></div>
                  <div className="orbit-ring orbit-ring-2"></div>
                  <div className="orbit-ring orbit-ring-3"></div>
                  <div className="satellite"></div>
                  {launchStatus === 'launching' && <div className="launch-trail"></div>}
                </div>
                
                {/* Individual countdown units */}
                <div className="countdown-units">
                  <div className="countdown-unit">
                    <div className="countdown-number">{countdownParts.days}</div>
                    <div className="countdown-unit-label">DAYS</div>
                  </div>
                  <div className="countdown-separator">:</div>
                  <div className="countdown-unit">
                    <div className="countdown-number">{countdownParts.hours.toString().padStart(2, '0')}</div>
                    <div className="countdown-unit-label">HOURS</div>
                  </div>
                  <div className="countdown-separator">:</div>
                  <div className="countdown-unit">
                    <div className="countdown-number">{countdownParts.minutes.toString().padStart(2, '0')}</div>
                    <div className="countdown-unit-label">MINUTES</div>
                  </div>
                  <div className="countdown-separator">:</div>
                  <div className="countdown-unit">
                    <div className={`countdown-number ${countdownParts.seconds % 2 === 0 ? 'pulse' : ''}`}>
                      {countdownParts.seconds.toString().padStart(2, '0')}
                    </div>
                    <div className="countdown-unit-label">SECONDS</div>
                  </div>
                </div>
                
                <div className="countdown-display-legacy">{countdown}</div>
              </div>
              <div className="launch-time">
                {launch.launchTime ? format(new Date(launch.launchTime), 'PPP • pp zzz') : 'Time TBD'}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button 
              className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              <span className="tab-icon">📋</span>
              Details
            </button>
            <button 
              className={`tab-button ${activeTab === 'weather' ? 'active' : ''}`}
              onClick={() => setActiveTab('weather')}
            >
              <span className="tab-icon">🌤️</span>
              Weather
            </button>
            <button 
              className={`tab-button ${activeTab === 'tracking' ? 'active' : ''}`}
              onClick={() => setActiveTab('tracking')}
            >
              <span className="tab-icon">📡</span>
              Tracking
            </button>
            <button 
              className={`tab-button ${activeTab === 'trajectory' ? 'active' : ''}`}
              onClick={() => setActiveTab('trajectory')}
            >
              <span className="tab-icon">🛰️</span>
              Trajectory
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'details' && (
              <div className="details-tab">
                <div className="mission-description">
                  {launch.description || `${launch.name || 'Mission'} launching from ${launch.launchSite || 'Launch Site'}`}
                </div>
                
                <div className="details-grid">
                  <div className="detail-card">
                    <div className="detail-icon">🚀</div>
                    <div className="detail-content">
                      <div className="detail-label">Vehicle</div>
                      <div className="detail-value">{launch.vehicleType || 'TBD'}</div>
                    </div>
                  </div>
                  
                  <div className="detail-card">
                    <div className="detail-icon">📍</div>
                    <div className="detail-content">
                      <div className="detail-label">Launch Site</div>
                      <div className="detail-value">{launch.launchSite || 'Cape Canaveral'}</div>
                    </div>
                  </div>
                  
                  <div className="detail-card">
                    <div className="detail-icon">🛰️</div>
                    <div className="detail-content">
                      <div className="detail-label">Mission Type</div>
                      <div className="detail-value">{launch.missionType || 'Orbital'}</div>
                    </div>
                  </div>
                  
                  {launch.payload && (
                    <div className="detail-card">
                      <div className="detail-icon">📦</div>
                      <div className="detail-content">
                        <div className="detail-label">Payload</div>
                        <div className="detail-value">{launch.payload}</div>
                      </div>
                    </div>
                  )}
                  
                  {launch.orbit && (
                    <div className="detail-card">
                      <div className="detail-icon">🌍</div>
                      <div className="detail-content">
                        <div className="detail-label">Target Orbit</div>
                        <div className="detail-value">{launch.orbit}</div>
                      </div>
                    </div>
                  )}
                  
                  {launch.launchPad && (
                    <div className="detail-card">
                      <div className="detail-icon">🏗️</div>
                      <div className="detail-content">
                        <div className="detail-label">Launch Pad</div>
                        <div className="detail-value">{launch.launchPad}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {activeTab === 'weather' && (
              <div className="weather-tab">
                {weather ? (
                  <div className="weather-grid">
                    <div className="weather-card">
                      <div className="weather-icon">🌡️</div>
                      <div className="weather-label">Temperature</div>
                      <div className="weather-value">{weather.temperature}°F</div>
                    </div>
                    
                    <div className="weather-card">
                      <div className="weather-icon">💨</div>
                      <div className="weather-label">Wind Speed</div>
                      <div className="weather-value">{weather.windSpeed} mph</div>
                    </div>
                    
                    <div className="weather-card">
                      <div className="weather-icon">☁️</div>
                      <div className="weather-label">Conditions</div>
                      <div className="weather-value">{weather.conditions}</div>
                    </div>
                    
                    {weather.weatherGo !== undefined && (
                      <div className="weather-card">
                        <div className="weather-icon">✅</div>
                        <div className="weather-label">Weather GO</div>
                        <div className="weather-value">{weather.weatherGo}%</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="weather-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading weather data...</p>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'tracking' && (
              <div className="tracking-tab">
                <div className="tracking-info">
                  <p className="tracking-message">
                    Track this launch in real-time with our mission control dashboard.
                    Get notifications for key events and milestones.
                  </p>
                  
                  <div className="mission-timeline">
                    <h4 className="timeline-title">Mission Timeline</h4>
                    <div className="timeline-items">
                      <div className="timeline-item active">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                          <span className="timeline-time">T-0:00</span>
                          <span className="timeline-event">Launch</span>
                        </div>
                      </div>
                      <div className="timeline-item">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                          <span className="timeline-time">T+0:02</span>
                          <span className="timeline-event">Max Q</span>
                        </div>
                      </div>
                      <div className="timeline-item">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                          <span className="timeline-time">T+0:09</span>
                          <span className="timeline-event">Stage Separation</span>
                        </div>
                      </div>
                      <div className="timeline-item">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                          <span className="timeline-time">T+0:15</span>
                          <span className="timeline-event">Orbit Insertion</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="tracking-features">
                    <div className="tracking-feature">
                      <span className="feature-icon">🎯</span>
                      <span>Real-time trajectory</span>
                    </div>
                    <div className="tracking-feature">
                      <span className="feature-icon">📊</span>
                      <span>Telemetry data</span>
                    </div>
                    <div className="tracking-feature">
                      <span className="feature-icon">🔔</span>
                      <span>Event notifications</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'trajectory' && (
              <div className="trajectory-tab">
                <div className="trajectory-visualization">
                  <div className="orbital-display">
                    <div className="earth-container">
                      <div className="earth">
                        <div className="earth-grid"></div>
                        <div className="continents"></div>
                      </div>
                      <div className="atmosphere"></div>
                    </div>
                    
                    <div className="trajectory-path">
                      <div className="launch-arc"></div>
                      <div className="orbital-path"></div>
                      <div className="rocket-position">🚀</div>
                    </div>
                    
                    <div className="trajectory-info">
                      <div className="altitude-display">
                        <span className="label">Target Altitude:</span>
                        <span className="value">{launch.orbit || 'TBD'}</span>
                      </div>
                      <div className="inclination-display">
                        <span className="label">Inclination:</span>
                        <span className="value">{launch.missionType === 'Communications' ? '0°' : '51.6°'}</span>
                      </div>
                      <div className="velocity-display">
                        <span className="label">Target Velocity:</span>
                        <span className="value">7.8 km/s</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mission-phases">
                    <h4>Mission Phases</h4>
                    <div className="phases-list">
                      <div className="phase-item">
                        <div className="phase-icon">🔥</div>
                        <div className="phase-details">
                          <div className="phase-name">Ignition & Liftoff</div>
                          <div className="phase-time">T+0:00</div>
                        </div>
                      </div>
                      <div className="phase-item">
                        <div className="phase-icon">⚡</div>
                        <div className="phase-details">
                          <div className="phase-name">Max Q</div>
                          <div className="phase-time">T+0:01:20</div>
                        </div>
                      </div>
                      <div className="phase-item">
                        <div className="phase-icon">🔄</div>
                        <div className="phase-details">
                          <div className="phase-name">Stage Separation</div>
                          <div className="phase-time">T+0:02:30</div>
                        </div>
                      </div>
                      <div className="phase-item">
                        <div className="phase-icon">🛰️</div>
                        <div className="phase-details">
                          <div className="phase-name">Payload Deployment</div>
                          <div className="phase-time">T+0:15:00</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="modal-actions">
            {launch.streamUrl && (
              <a
                href={launch.streamUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="action-button stream-button"
              >
                <span className="button-icon">🎥</span>
                Watch Live
              </a>
            )}
            
            <button onClick={addToCalendar} className="action-button calendar-button">
              <span className="button-icon">📅</span>
              Add to Calendar
            </button>
            
            <button onClick={shareLaunch} className="action-button share-button">
              <span className="button-icon">🔗</span>
              Share
            </button>
            
            <button 
              onClick={() => {
                setIsAudioEnabled(!isAudioEnabled);
                if (audioRef.current) {
                  if (isAudioEnabled) {
                    audioRef.current.pause();
                  } else {
                    audioRef.current.play().catch(e => console.log('Audio play failed:', e));
                  }
                }
              }} 
              className="action-button audio-button"
            >
              <span className="button-icon">{isAudioEnabled ? '🔊' : '🔇'}</span>
              {isAudioEnabled ? 'Mute' : 'Audio'}
            </button>
          </div>
        </div>
      </>
  );

  // Always render as modal overlay for consistent experience
  return (
    <div className="launch-modal-overlay" onClick={onClose}>
      {showLaunchAnimation && (
        <div className="rocket-launch-animation">
          <div className="rocket">🚀</div>
          <div className="exhaust"></div>
        </div>
      )}
      
      <div 
        className="launch-modal-content" 
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          className="modal-close-button"
          onClick={onClose}
          aria-label="Close modal"
        >
          ✕
        </button>
        {renderModalContent()}
      </div>
    </div>
  );
};

export default LaunchModal;